"use client";

import { Suspense, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { z } from "zod";

import { clientAuth } from "@billingnow/utilities/auth-client";
import { parseCatchError } from "@/utils";

import {
  AppleLogo,
  AuthLeftPanel,
  AuthPageSkeleton,
  AuthTermsFooter,
  AuthUtilitiesRow,
  EMAIL_RE,
  GoogleLogo,
  inputClass,
  labelClass,
  nunitoSans,
  primaryBtnClass,
  secondaryBtnClass,
} from "../auth-ui";
import { useSearchParams } from "next/navigation";
import { cn } from "@/utils";

const signinSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Please enter your password"),
});

const magicLinkSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type SigninSchema = z.infer<typeof signinSchema>;
type MagicLinkSchema = z.infer<typeof magicLinkSchema>;

function SignUpFooter({ className }: { className?: string }) {
  const t = useTranslations("auth.signin");
  return (
    <p className={cn("text-center text-sm text-[color:var(--si-body-2)]", className)}>
      {t("noAccount")}{" "}
      <a href="/signup" className="font-extrabold text-[color:var(--si-ink)] no-underline">
        {t("createOne")}
      </a>
    </p>
  );
}

type View = "main" | "magic" | "sent";

function SigninForm() {
  const t = useTranslations("auth.signin");
  const searchParams = useSearchParams();
  const pathname = searchParams.get("callbackUrl") || "/";
  const [isLoading, setIsLoading] = useState(false);
  // "main" = Google + email/password. "magic" = the email-link view the
  // secondary button opens (enter email → link is sent; unknown emails get an
  // account created automatically by the magic-link plugin). "sent" = the
  // confirmation card with the resend cooldown.
  const [view, setView] = useState<View>("main");
  const [anim, setAnim] = useState<"in" | "out">("in");
  const [magicLinkSentTo, setMagicLinkSentTo] = useState<string | null>(null);
  const [resendIn, setResendIn] = useState(0);
  const transitionTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (transitionTimer.current) window.clearTimeout(transitionTimer.current);
    };
  }, []);

  // 30s client-side cooldown after a resend.
  useEffect(() => {
    if (resendIn <= 0) return;
    const id = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [resendIn]);

  // Slide the current step out (250ms), then the next one in (500ms).
  const go = (next: View) => {
    setAnim("out");
    if (transitionTimer.current) window.clearTimeout(transitionTimer.current);
    transitionTimer.current = window.setTimeout(() => {
      setView(next);
      setAnim("in");
    }, 250);
  };

  const { register, handleSubmit, watch } = useForm<SigninSchema>({
    resolver: zodResolver(signinSchema),
    defaultValues: { email: "", password: "" },
  });

  const {
    register: registerMagicLink,
    handleSubmit: handleSubmitMagicLink,
    watch: watchMagicLink,
  } = useForm<MagicLinkSchema>({
    resolver: zodResolver(magicLinkSchema),
    defaultValues: { email: "" },
  });

  const loginEmail = watch("email");
  const loginPassword = watch("password");
  const canLogin = EMAIL_RE.test(loginEmail) && loginPassword.length > 0;
  const canSendMagic = EMAIL_RE.test(watchMagicLink("email"));

  const onSubmit = async ({ email, password }: SigninSchema) => {
    setIsLoading(true);
    try {
      const result = await clientAuth.signIn.email({
        email,
        password,
        callbackURL: pathname,
      });

      if (result.error) {
        toast.error(t("toastInvalidCredentials"));
        return;
      }
    } catch (error) {
      toast.error(parseCatchError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const sendMagicLink = async (email: string): Promise<boolean> => {
    try {
      const result = await clientAuth.signIn.magicLink({
        email,
        callbackURL: pathname,
      });
      if (result.error) {
        toast.error(result?.error?.message || t("toastSignInFailed"));
        return false;
      }
      return true;
    } catch (error) {
      toast.error(parseCatchError(error));
      return false;
    }
  };

  const onSubmitMagicLink = async ({ email }: MagicLinkSchema) => {
    setIsLoading(true);
    const ok = await sendMagicLink(email);
    setIsLoading(false);
    if (ok) {
      setMagicLinkSentTo(email);
      setResendIn(0);
      go("sent");
    }
  };

  const handleResend = async () => {
    if (resendIn > 0 || !magicLinkSentTo) return;
    setResendIn(30);
    const ok = await sendMagicLink(magicLinkSentTo);
    if (!ok) setResendIn(0);
  };

  const handleGoogleSignin = async () => {
    setIsLoading(true);
    try {
      await clientAuth.signIn.social({
        provider: "google",
        callbackURL: pathname,
      });
    } catch (error) {
      toast.error(parseCatchError(error));
      setIsLoading(false);
    }
  };

  const handleAppleSignin = async () => {
    setIsLoading(true);
    try {
      await clientAuth.signIn.social({
        provider: "apple",
        callbackURL: pathname,
      });
    } catch (error) {
      toast.error(parseCatchError(error));
      setIsLoading(false);
    }
  };

  const backToSignInLink = (
    <button
      type="button"
      onClick={() => {
        setMagicLinkSentTo(null);
        go("main");
      }}
      className="mt-7 inline-block self-start text-sm font-bold text-[color:var(--si-ink)] transition-colors hover:text-[color:var(--si-ink-hover)]"
    >
      ← {t("backToSignIn")}
    </button>
  );

  return (
    <div
      className={cn(
        nunitoSans.className,
        "signin-scope relative flex min-h-screen w-full flex-wrap overflow-hidden bg-[color:var(--si-page)] text-[color:var(--si-ink)] transition-colors duration-300",
      )}
    >
      <AuthLeftPanel namespace="auth.signin" footerKey="trustedBy" />

      {/* Right auth panel */}
      <div className="relative box-border flex min-w-[320px] flex-[1_1_460px] flex-col px-[clamp(24px,5vw,72px)] py-[clamp(24px,4vw,44px)]">
        <div className="mx-auto flex w-full max-w-[440px] flex-1 flex-col justify-center">
          <div
            data-signin-anim
            style={{
              animation:
                anim === "out"
                  ? "signin-step-out .25s ease-in both"
                  : "signin-step-in .5s cubic-bezier(.22,1,.36,1) both",
            }}
            className="flex flex-col"
          >
            {/* Header (all states) */}
            <h2 className="m-0 text-[clamp(24px,3vw,30px)] font-extrabold text-[color:var(--si-ink)]">{t("title")}</h2>
            <p className="mb-0 mt-2 text-[15px] text-[color:var(--si-body-2)]">{t("subtitle")}</p>

            {view === "main" && (
              <>
                <div className="mt-8 flex flex-col gap-3">
                  <button
                    type="button"
                    onClick={handleGoogleSignin}
                    disabled={isLoading}
                    className={secondaryBtnClass}
                  >
                    <GoogleLogo />
                    {t("continueWithGoogle")}
                  </button>

                  <button
                    type="button"
                    onClick={handleAppleSignin}
                    disabled={isLoading}
                    className={secondaryBtnClass}
                  >
                    <AppleLogo />
                    {t("continueWithApple")}
                  </button>

                  <button
                    type="button"
                    onClick={() => go("magic")}
                    disabled={isLoading}
                    className={secondaryBtnClass}
                  >
                    {t("emailLinkButton")}
                  </button>
                </div>

                {/* Divider */}
                <div className="my-[26px] flex items-center gap-4">
                  <div className="h-px flex-1 bg-[color:var(--si-hairline)]" />
                  <span className="text-xs font-bold uppercase tracking-[1px] text-[color:var(--si-muted)]">
                    {t("or")}
                  </span>
                  <div className="h-px flex-1 bg-[color:var(--si-hairline)]" />
                </div>

                {/* Email + password */}
                <form onSubmit={handleSubmit(onSubmit)}>
                  <label htmlFor="email" className={labelClass}>
                    {t("emailLabel")}
                  </label>
                  <input
                    id="email"
                    type="email"
                    placeholder={t("emailPlaceholder")}
                    {...register("email")}
                    disabled={isLoading}
                    className={inputClass}
                  />

                  <label htmlFor="password" className={cn(labelClass, "mt-[18px]")}>
                    {t("passwordLabel")}
                  </label>
                  <input
                    id="password"
                    type="password"
                    placeholder={t("passwordPlaceholder")}
                    {...register("password")}
                    disabled={isLoading}
                    className={inputClass}
                  />

                  <button type="submit" disabled={isLoading || !canLogin} className={primaryBtnClass}>
                    {isLoading ? t("signingIn") : t("signInButton")}
                  </button>
                </form>

                <SignUpFooter className="mt-[22px]" />
              </>
            )}

            {view === "magic" && (
              <>
                {backToSignInLink}
                <p className="mb-0 mt-5 text-[14.5px] leading-[1.6] text-[color:var(--si-body)] [text-wrap:pretty]">
                  {t("magicLinkHint")}
                </p>

                <form onSubmit={handleSubmitMagicLink(onSubmitMagicLink)}>
                  <label htmlFor="magic-email" className={cn(labelClass, "mt-[22px]")}>
                    {t("emailLabel")}
                  </label>
                  <input
                    id="magic-email"
                    type="email"
                    placeholder={t("emailPlaceholder")}
                    autoFocus
                    {...registerMagicLink("email")}
                    disabled={isLoading}
                    className={inputClass}
                  />
                  <button type="submit" disabled={isLoading || !canSendMagic} className={primaryBtnClass}>
                    {isLoading ? t("sendingLink") : t("sendMagicLink")}
                  </button>
                </form>

                <SignUpFooter className="mt-[22px]" />
              </>
            )}

            {view === "sent" && (
              <>
                {backToSignInLink}

                {/* Confirmation card */}
                <div
                  data-signin-anim
                  className="mt-5 overflow-hidden rounded-[18px] border border-[color:var(--si-hairline)] bg-[color:var(--si-surface)] transition-colors duration-300"
                  style={{ animation: "signin-pop .4s ease both", boxShadow: "var(--si-card-shadow)" }}
                >
                  <div className="h-1 bg-[color:var(--si-primary-bg)]" />
                  <div className="box-border flex flex-col items-center px-[22px] pb-[22px] pt-6 text-center">
                    <div className="relative h-12 w-12 flex-none">
                      <div
                        className="flex h-12 w-12 items-center justify-center rounded-[14px] bg-[color:var(--si-primary-bg)] text-[color:var(--si-primary-fg)] transition-colors duration-300"
                        style={{ boxShadow: "var(--si-chip-shadow)" }}
                      >
                        <svg
                          width="22"
                          height="22"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.7"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="3" y="5" width="18" height="14" rx="2" />
                          <path d="m3 7 9 6 9-6" />
                        </svg>
                      </div>
                      <div className="absolute -bottom-1.5 -right-1.5 flex h-[22px] w-[22px] items-center justify-center rounded-full border-[3px] border-[color:var(--si-badge-ring)] bg-[#4ADE80] text-[10px] font-black text-[#0F172A]">
                        ✓
                      </div>
                    </div>
                    <div className="mt-3 text-[16.5px] font-extrabold text-[color:var(--si-ink)]">
                      {t("magicLinkSentTitle")}
                    </div>
                    <div className="mt-0.5 text-[13.5px] text-[color:var(--si-body-2)]">
                      {t.rich("magicLinkSentBody", {
                        email: magicLinkSentTo ?? "",
                        b: (chunks) => <b className="text-[color:var(--si-ink)]">{chunks}</b>,
                      })}
                    </div>
                    <div className="mt-2.5 text-[12.5px] text-[color:var(--si-muted)]">{t("linkExpiryNote")}</div>
                    <div className="mt-3.5 flex flex-wrap justify-center gap-2.5">
                      <button
                        type="button"
                        onClick={() => go("magic")}
                        className="inline-flex items-center justify-center rounded-full border border-[color:var(--si-border)] bg-[color:var(--si-surface)] px-[18px] py-[9px] text-[13.5px] font-bold text-[color:var(--si-ink)] transition-colors duration-200 hover:border-[color:var(--si-ink)]"
                      >
                        {t("useDifferentEmail")}
                      </button>
                      <button
                        type="button"
                        onClick={handleResend}
                        disabled={resendIn > 0}
                        className={cn(
                          "inline-flex items-center justify-center rounded-full px-3.5 py-[9px] text-[13.5px] font-bold transition-colors duration-200",
                          resendIn > 0
                            ? "cursor-default text-[color:var(--si-muted)]"
                            : "text-[color:var(--si-resend)] hover:text-[color:var(--si-ink)]",
                        )}
                      >
                        {resendIn > 0 ? t("resendCountdown", { seconds: resendIn }) : t("resendLink")}
                      </button>
                    </div>
                  </div>
                </div>

                <SignUpFooter className="mt-[22px]" />
              </>
            )}

            <AuthTermsFooter namespace="auth.signin" />
          </div>
        </div>

        <AuthUtilitiesRow />
      </div>
    </div>
  );
}

export default function SigninPage() {
  return (
    <Suspense fallback={<AuthPageSkeleton />}>
      <SigninForm />
    </Suspense>
  );
}
